// import axios from '@/lib/axios';
import axiosInstance from '@/lib/axios';

export interface StatusConfig {
  id: string;
  name: string;
  color: string;
  lightColor?: string;
  actualColor?: string;
  description?: string;
  isDefault: boolean;
  isActive: boolean;
  order: number;
  category: 'jobPosting' | 'candidate' | 'interview' | 'project' | 'task';
  createdAt: string;
  updatedAt: string;
  isSystem?: boolean;
}

export const getStatuses = async (): Promise<StatusConfig[]> => {
  try {
    const res = await axiosInstance.get('/status');

    let statusData: any[] = [];

    // Handle different response formats
    if (Array.isArray(res.data)) {
      statusData = res.data;
    } else if (res.data && Array.isArray(res.data.data)) {
      statusData = res.data.data;
    } else if (res.data && res.data.statuses && Array.isArray(res.data.statuses)) {
      statusData = res.data.statuses;
    } else if (res.data && res.data.result && Array.isArray(res.data.result)) {
      statusData = res.data.result;
    } else {
      console.warn('Unexpected response format:', res.data);
      statusData = [];
    }

    // Ensure each status has the required fields
    const processedStatuses = statusData.map((status: any) => ({
      id: status.id || status._id || status.statusId,
      name: status.name || status.statusName,
      color: status.color || '#3B82F6',
      description: status.description || '',
      isDefault: status.isDefault || status.default || false,
      isActive: status.isActive !== undefined ? status.isActive : true,
      order: status.order || 0,
      category: status.category || 'jobPosting',
      createdAt: status.createdAt || status.created_at || new Date().toISOString(),
      updatedAt: status.updatedAt || status.updated_at || new Date().toISOString(),
      isSystem: status.isSystem || false,
    }));
    return processedStatuses;
  } catch (error) {
    console.error('Error fetching statuses:', error);
    throw error;
  }
};

export const getActiveStatuses = async (): Promise<StatusConfig[]> => {
  try {
    const allStatuses = await getStatuses();

    // Filter to only return active statuses
    const activeStatuses = allStatuses.filter(status => status.isActive === true);

    return activeStatuses;
  } catch (error) {
    console.error('Error fetching active statuses:', error);
    throw error;
  }
};

export const createStatus = async (data: Partial<StatusConfig>): Promise<StatusConfig> => {
  try {
    const res = await axiosInstance.post('/status', data);
    // Handle different response formats
    let statusData = res.data;
    if (res.data && res.data.data) {
      statusData = res.data.data;
    } else if (res.data && res.data.status) {
      statusData = res.data.status;
    }

    // Ensure the response has the required fields
    const processedStatus = {
      id: statusData.id || statusData._id || statusData.statusId,
      name: statusData.name || statusData.statusName,
      color: statusData.color || '#3B82F6',
      description: statusData.description || '',
      isDefault: statusData.isDefault || statusData.default || false,
      isActive: statusData.isActive !== undefined ? statusData.isActive : true,
      order: statusData.order || 0,
      category: statusData.category || 'jobPosting',
      createdAt: statusData.createdAt || statusData.created_at || new Date().toISOString(),
      updatedAt: statusData.updatedAt || statusData.updated_at || new Date().toISOString(),
      isSystem: statusData.isSystem || false,
    };

    return processedStatus;
  } catch (error) {
    console.error('Error creating status:', error);
    throw error;
  }
};

export const updateStatus = async (id: string, data: Partial<StatusConfig>): Promise<StatusConfig> => {
  try {
    const res = await axiosInstance.patch(`/status/${id}`, data);

    // Handle different response formats
    let statusData = res.data;
    if (res.data && res.data.data) {
      statusData = res.data.data;
    } else if (res.data && res.data.status) {
      statusData = res.data.status;
    }

    // Ensure the response has the required fields
    const processedStatus = {
      id: statusData.id || statusData._id || statusData.statusId || id,
      name: statusData.name || statusData.statusName,
      color: statusData.color || '#3B82F6',
      description: statusData.description || '',
      isDefault: statusData.isDefault || statusData.default || false,
      isActive: statusData.isActive !== undefined ? statusData.isActive : true,
      order: statusData.order || 0,
      category: statusData.category || 'jobPosting',
      createdAt: statusData.createdAt || statusData.created_at || new Date().toISOString(),
      updatedAt: statusData.updatedAt || statusData.updated_at || new Date().toISOString(),
      isSystem: statusData.isSystem || false,
    };

    return processedStatus;
  } catch (error) {
    console.error('Error updating status:', error);
    throw error;
  }
};

export const deleteStatus = async (id: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/status/${id}`);

  } catch (error) {
    console.error('Error deleting status:', error);
    throw error;
  }
};

export const toggleStatusActive = async (id: string, isActive: boolean): Promise<StatusConfig> => {
  try {
    const res = await axiosInstance.patch(`/status/${id}`, { isActive });

    // Handle different response formats
    let statusData = res.data;
    if (res.data && res.data.data) {
      statusData = res.data.data;
    } else if (res.data && res.data.status) {
      statusData = res.data.status;
    }

    // Ensure the response has the required fields
    const processedStatus = {
      id: statusData.id || statusData._id || statusData.statusId || id,
      name: statusData.name || statusData.statusName,
      color: statusData.color || '#3B82F6',
      description: statusData.description || '',
      isDefault: statusData.isDefault || statusData.default || false,
      isActive: statusData.isActive !== undefined ? statusData.isActive : isActive,
      order: statusData.order || 0,
      category: statusData.category || 'jobPosting',
      createdAt: statusData.createdAt || statusData.created_at || new Date().toISOString(),
      updatedAt: statusData.updatedAt || statusData.updated_at || new Date().toISOString(),
      isSystem: statusData.isSystem || false,
    };

    return processedStatus;
  } catch (error) {
    console.error('Error toggling status active:', error);
    throw error;
  }
};

export const getStatusesByCategory = async (category: string): Promise<StatusConfig[]> => {
  try {
    const allActiveStatuses = await getActiveStatuses();

    // Filter by category (all statuses are already active)
    const categoryStatuses = allActiveStatuses.filter((status: StatusConfig) =>
      status.category === category
    );

    return categoryStatuses;
  } catch (error) {
    console.error('Error fetching statuses by category:', error);
    throw error;
  }
}; 